import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Download, Loader2, FileText, Clock, AlertCircle, Check, MessageSquareText, X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function fmtSize(bytes) {
  if (!bytes) return "";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let v = bytes;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function FeedbackForm({ token, file, clientName, setClientName, clientEmail, setClientEmail, onDone }) {
  const [mode, setMode] = useState(null);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (decision) => {
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/public/shares/${token}/feedback`, {
        file_id: file.file_id, decision, comment, client_name: clientName, client_email: clientEmail,
      });
      onDone(data.approval);
    } catch (e) {
      setError(e.response?.data?.detail || "Senden fehlgeschlagen — bitte erneut versuchen");
    } finally { setBusy(false); }
  };

  if (!mode) {
    return (
      <div className="flex flex-wrap gap-2 px-3.5 pb-3.5">
        <button
          data-testid={`public-approve-${file.file_id}`}
          onClick={() => setMode("approved")}
          className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-semibold px-3.5 py-2.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition"
        >
          <Check className="h-4 w-4" /> Freigeben
        </button>
        <button
          data-testid={`public-changes-${file.file_id}`}
          onClick={() => setMode("changes")}
          className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-semibold px-3.5 py-2.5 rounded-lg bg-white border border-amber-400 text-amber-700 hover:bg-amber-50 transition"
        >
          <MessageSquareText className="h-4 w-4" /> Änderung anfordern
        </button>
      </div>
    );
  }

  return (
    <div className="px-3.5 pb-3.5 space-y-2" data-testid={`public-feedback-form-${file.file_id}`}>
      <div className="flex items-center justify-between">
        <p className={`text-xs font-bold ${mode === "approved" ? "text-emerald-700" : "text-amber-700"}`}>
          {mode === "approved" ? "✓ Datei freigeben" : "Änderung anfordern"}
        </p>
        <button onClick={() => { setMode(null); setError(""); }} className="text-neutral-400 hover:text-neutral-600"><X className="h-4 w-4" /></button>
      </div>
      <input
        data-testid={`public-name-input-${file.file_id}`}
        value={clientName}
        onChange={(e) => setClientName(e.target.value)}
        placeholder="Ihr Name (optional)"
        className="w-full bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-2.5 text-sm"
      />
      <input
        data-testid={`public-email-input-${file.file_id}`}
        type="email"
        value={clientEmail}
        onChange={(e) => setClientEmail(e.target.value)}
        placeholder="Ihre E-Mail (optional — für eine Bestätigung)"
        className="w-full bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-2.5 text-sm"
      />
      <textarea
        data-testid={`public-comment-input-${file.file_id}`}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        rows={2}
        placeholder={mode === "approved" ? "Kommentar (optional)" : "Was soll geändert werden? *"}
        className="w-full bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-2.5 text-sm resize-none"
      />
      {error && <p className="text-xs text-rose-600">{error}</p>}
      <button
        data-testid={`public-feedback-submit-${file.file_id}`}
        onClick={() => submit(mode)}
        disabled={busy || (mode === "changes" && !comment.trim())}
        className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-white text-sm font-semibold disabled:opacity-50 ${mode === "approved" ? "bg-emerald-600" : "bg-amber-500"}`}
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
        {mode === "approved" ? "Freigabe bestätigen" : "Änderungswunsch senden"}
      </button>
    </div>
  );
}

function ApprovalBanner({ approval }) {
  const ok = approval.decision === "approved";
  return (
    <div className={`mx-3.5 mb-3.5 rounded-lg px-3 py-2.5 text-xs ${ok ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"}`} data-testid="public-approval-banner">
      <p className="font-bold">{ok ? "🟢 Freigegeben" : "🟠 Änderung angefordert"}
        {approval.client_name && ` — ${approval.client_name}`}
        {approval.at && ` · ${new Date(approval.at).toLocaleDateString("de-DE")}`}
      </p>
      {approval.comment && <p className="mt-1 italic">„{approval.comment}"</p>}
    </div>
  );
}

export default function PublicShare() {
  const { token } = useParams();
  const [pkg, setPkg] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/public/shares/${token}`)
      .then(({ data }) => setPkg(data))
      .catch((e) => setError(e.response?.data?.detail || "Freigabe nicht gefunden"))
      .finally(() => setLoading(false));
  }, [token]);

  const fileUrl = (fid) => `${BACKEND_URL}/api/public/shares/${token}/files/${fid}`;

  const [dlBusy, setDlBusy] = useState(null);
  const downloadFile = async (f) => {
    if (dlBusy) return;
    setDlBusy({ id: f.file_id, pct: 0 });
    try {
      const { data: blob } = await axios.get(`${fileUrl(f.file_id)}?download=1`, {
        responseType: "blob",
        timeout: 600000,
        onDownloadProgress: (e) => {
          if (e.total) setDlBusy({ id: f.file_id, pct: Math.round((e.loaded / e.total) * 100) });
        },
      });
      const name = f.filename || "datei";
      const saveViaAnchor = () => {
        const u = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = u;
        a.download = name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(u), 30000);
        toast.success(`„${name}“ heruntergeladen ✅`);
      };
      const isMobile = window.matchMedia?.("(pointer: coarse)")?.matches;
      const file = new File([blob], name, { type: blob.type || "application/octet-stream" });
      if (isMobile && typeof navigator.canShare === "function" && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file] });
          toast.success("Im Teilen-Menü »Sichern« wählen — fertig ✅");
        } catch (err) {
          if (err?.name === "AbortError") return;
          saveViaAnchor();
        }
      } else {
        saveViaAnchor();
      }
    } catch (err) {
      let detail = "";
      try {
        detail = JSON.parse(await err.response?.data?.text?.())?.detail || "";
      } catch { /* noop */ }
      toast.error(detail || "Download fehlgeschlagen — bitte erneut versuchen");
    } finally {
      setDlBusy(null);
    }
  };

  const setApproval = (fid, approval) => {
    setPkg((p) => ({ ...p, files: p.files.map((f) => (f.file_id === fid ? { ...f, approval } : f)) }));
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-neutral-900">
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-2xl mx-auto px-5 py-4 flex items-center gap-3">
          <img src="/innovationshafen-favicon-orange.png" alt="" className="h-9 w-9 rounded-xl" />
          <div className="leading-tight">
            <p className="font-head font-bold text-sm">Dateifreigabe</p>
            <p className="text-[10px] text-neutral-400">Innovationshafen · SM-Regio</p>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-5 py-8">
        {loading ? (
          <div className="py-20 flex justify-center text-neutral-400"><Loader2 className="h-7 w-7 animate-spin" /></div>
        ) : error ? (
          <div data-testid="public-share-error" className="bg-white border border-neutral-200 rounded-2xl p-8 text-center">
            <AlertCircle className="h-8 w-8 text-rose-500 mx-auto" />
            <p className="font-head font-bold text-lg mt-3">{error}</p>
            <p className="text-sm text-neutral-400 mt-1">Bitte wende dich an deinen Ansprechpartner, um einen neuen Link zu erhalten.</p>
          </div>
        ) : (
          <>
            <h1 data-testid="public-share-title" className="font-head text-2xl sm:text-3xl font-bold tracking-tight">{pkg.title}</h1>
            {pkg.note && <p data-testid="public-share-note" className="text-sm text-neutral-600 mt-2 whitespace-pre-wrap">{pkg.note}</p>}
            {pkg.expires_at && (
              <p className="text-[11px] text-neutral-400 mt-2 flex items-center gap-1">
                <Clock className="h-3 w-3" /> Verfügbar bis {new Date(pkg.expires_at).toLocaleDateString("de-DE")}
              </p>
            )}
            <p className="text-xs text-neutral-500 mt-3 bg-white border border-neutral-200 rounded-lg px-3 py-2">
              💡 Bitte sichte die Dateien und gib je Datei eine Rückmeldung: <b>Freigeben</b> oder <b>Änderung anfordern</b> — das Team wird sofort benachrichtigt.
            </p>
            <div className="mt-6 space-y-4">
              {pkg.files.map((f) => (
                <div key={f.file_id} data-testid={`public-file-${f.file_id}`} className="bg-white border border-neutral-200 rounded-2xl overflow-hidden shadow-sm">
                  {f.kind === "video" ? (
                    <video src={fileUrl(f.file_id)} controls playsInline className="w-full max-h-[60vh] bg-black" />
                  ) : f.kind === "image" ? (
                    <img src={fileUrl(f.file_id)} alt={f.filename} className="w-full max-h-[60vh] object-contain bg-neutral-100" />
                  ) : null}
                  <div className="p-3.5 flex items-center gap-3">
                    {f.kind === "file" && <FileText className="h-5 w-5 text-neutral-400 shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold truncate">{f.filename || "Datei"}</p>
                      {f.size > 0 && <p className="text-[11px] text-neutral-400">{fmtSize(f.size)}</p>}
                    </div>
                    <button
                      onClick={() => downloadFile(f)}
                      disabled={!!dlBusy}
                      data-testid={`public-download-${f.file_id}`}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2.5 rounded-lg bg-orange-500 text-white hover:bg-orange-600 transition shrink-0 disabled:opacity-60"
                    >
                      {dlBusy?.id === f.file_id ? (
                        <span className="flex items-center gap-1.5" data-testid={`public-download-progress-${f.file_id}`}>
                          <Loader2 className="h-4 w-4 animate-spin" />{dlBusy.pct > 0 ? `${dlBusy.pct}%` : "Lädt…"}
                        </span>
                      ) : (
                        <><Download className="h-4 w-4" /> Download</>
                      )}
                    </button>
                  </div>
                  {f.approval ? (
                    <ApprovalBanner approval={f.approval} />
                  ) : (
                    <FeedbackForm token={token} file={f} clientName={clientName} setClientName={setClientName}
                      clientEmail={clientEmail} setClientEmail={setClientEmail}
                      onDone={(a) => setApproval(f.file_id, a)} />
                  )}
                </div>
              ))}
            </div>
            <div className="mt-10 flex flex-col items-center gap-2">
              <p className="text-[10px] text-neutral-400">Bereitgestellt über</p>
              <a href="https://innovationshafen.net" target="_blank" rel="noreferrer" className="bg-white border border-neutral-200 rounded-full px-4 py-2">
                <img src="/innovationshafen-logo.png" alt="Innovationshafen" className="h-4 w-auto" />
              </a>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
